import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "../../../assets/css/product-studio-edit.css";
import NotificationComponent from "../../../components/Alert/alert";
import CatalogBarcodePrintModal from "../../../components/Print/CatalogBarcodePrintModal";
import {
  buildCatalogPrintItemsFromWorkspace,
  isSerializedProduct,
} from "../../../components/Print/catalogBarcodePrintItems";
import {
  useAdjustCatalogProductInventoryMutation,
  useFetchCatalogInventoryHistoryQuery,
  useFetchCatalogProductWorkspaceQuery,
  useReplaceCatalogProductMediaMutation,
  useRestockCatalogProductMutation,
  useSaveCatalogProductStructureMutation,
  useUploadCatalogMediaAssetMutation,
  useUpdateCatalogProductCoreMutation,
  useUpdateCatalogProductPublishingMutation,
  useUpdateCatalogVariantsMutation,
} from "../../../api/api";
import {
  useFetchBrandsQuery,
  useFetchColorsQuery,
  useFetchLocationsQuery,
  useFetchTypesQuery,
} from "../../../api/cmsPanelsApi";
import { buildProductColors, pruneInvalidColorCovers } from "../../../utils/catalogColorCover";
import { applySharedPricingToVariantDrafts } from "../../../utils/catalogProductStructurePricing";
import {
  buildEnrichmentPayload,
  createEmptyMediaItem,
  getProductFamilyOptions,
} from "../../../utils/catalogProductStudioShared";
import { useTranslation } from "../../../hooks/useTranslation";

import {
  ALLOWED_UPLOAD_TYPES,
  MAX_PRODUCT_MEDIA,
  MAX_UPLOAD_BYTES,
  STATUS_TO_API,
  STUDIO_TABS,
  buildCorePayload,
  buildStructurePayload,
  fill,
  findColorOption,
  getStructureSnapshot,
  humanizeCatalogError,
  isFilledMedia,
  mapMediaDraftsToSaveItems,
  mapVariantDraftsToSaveItems,
  mapWorkspaceToCoreForm,
  mapWorkspaceToMediaDrafts,
  mapWorkspaceToStructureForm,
  mapWorkspaceToVariantDrafts,
  resolveTabFromHash,
  variantDraftHasUnsavedChanges,
} from "./studioEditModel";
import { Btn, EmptyState, Icon, Notice, Spinner } from "./StudioUi";
import { printCenterReceiptPath } from "../../printCenter/printCenterLinks";
import StudioShell from "./StudioShell";
import useUnsavedLinkGuard from "./useUnsavedLinkGuard";
import OverviewTab from "./tabs/OverviewTab";
import MediaTab from "./tabs/MediaTab";
import VariantsTab from "./tabs/VariantsTab";
import StockTab from "./tabs/StockTab";
import DetailsTab from "./tabs/DetailsTab";
import PublishingTab from "./tabs/PublishingTab";
import HistoryTab from "./tabs/HistoryTab";
import RestockDrawer from "./drawers/RestockDrawer";
import AdjustStockDrawer from "./drawers/AdjustStockDrawer";

const SECTIONS = ["core", "pricing", "variants", "media", "structure"];

const PRICING_FIELDS = ["price", "compareAtPrice", "cost"];
const pickPricing = (form) => ({
  price: form?.price ?? "",
  compareAtPrice: form?.compareAtPrice ?? "",
  cost: form?.cost ?? "",
});
const omitPricing = (form) => {
  const next = { ...form };
  PRICING_FIELDS.forEach((field) => delete next[field]);
  return next;
};

// Section fingerprints: two drafts with the same fingerprint save the same
// payload, so "dirty" means "saving would send something different".
const coreKey = (form) =>
  form ? JSON.stringify(buildCorePayload(form, buildEnrichmentPayload(form.enrichment))) : "";
const pricingKey = (form) => (form ? JSON.stringify(pickPricing(form)) : "");
const structureKey = (form) => (form ? getStructureSnapshot(form) : "");
const variantsKey = (drafts) => (drafts ? JSON.stringify(mapVariantDraftsToSaveItems(drafts)) : "");
const mediaKey = (drafts) => (drafts ? JSON.stringify(mapMediaDraftsToSaveItems(drafts)) : "");

const VARIANT_FIELDS = ["sku", "barcode", "status", "position", "isDefault", "price", "compareAtPrice", "cost"];

/**
 * Three-way merge for variant drafts after a refetch: a field the user has not
 * touched takes the server's new value (e.g. publishing flipped every status),
 * a field the user edited keeps the edit. New/removed variants follow the server.
 */
export const mergeVariantDrafts = (current, previousBase, next) => {
  const currentById = new Map((current || []).map((draft) => [draft.id, draft]));
  const baseById = new Map((previousBase || []).map((draft) => [draft.id, draft]));
  return next.map((serverDraft) => {
    const draft = currentById.get(serverDraft.id);
    const base = baseById.get(serverDraft.id);
    if (!draft || !base) return serverDraft;
    const merged = { ...serverDraft };
    VARIANT_FIELDS.forEach((field) => {
      if (draft[field] !== base[field]) merged[field] = draft[field];
    });
    return merged;
  });
};

const ProductStudioEditWorkspace = ({ productId }) => {
  const t = useTranslation();
  const tt = useCallback((key) => t(`productStudioEdit.${key}`), [t]);
  const location = useLocation();
  const navigate = useNavigate();
  const notificationRef = useRef(null);

  const [activeTab, setActiveTab] = useState(() => resolveTabFromHash(location.hash));
  const [coreForm, setCoreForm] = useState(null);
  const [pricingForm, setPricingForm] = useState(null);
  const [structureForm, setStructureForm] = useState(null);
  const [variantDrafts, setVariantDrafts] = useState(null);
  const [mediaDrafts, setMediaDrafts] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const baselineRef = useRef(null);
  const variantBaseDraftsRef = useRef(null);

  const [isSavingAll, setIsSavingAll] = useState(false);
  const [restockState, setRestockState] = useState({ open: false, variantId: null });
  const [adjustState, setAdjustState] = useState({ open: false, variantId: null });
  const [printState, setPrintState] = useState({ open: false, items: [], copies: null });
  const [focusVariantId, setFocusVariantId] = useState(null);
  // Opening stock saved by the create wizard that still needs labels (optional follow-up).
  const [createdReceipt, setCreatedReceipt] = useState(null);
  const [isStructureEditorOpen, setIsStructureEditorOpen] = useState(false);

  const {
    data: workspace,
    isFetching: isFetchingWorkspace,
    isError: isWorkspaceError,
    refetch,
  } = useFetchCatalogProductWorkspaceQuery(productId, { skip: !productId });

  const recentHistoryQuery = useFetchCatalogInventoryHistoryQuery(
    { productId, params: { limit: 5 } },
    { skip: !productId }
  );

  const [uploadCatalogMediaAsset, { isLoading: isUploadingMedia }] = useUploadCatalogMediaAssetMutation();
  const [updateCatalogProductCore] = useUpdateCatalogProductCoreMutation();
  const [saveCatalogProductStructure, { isLoading: isSavingStructure }] =
    useSaveCatalogProductStructureMutation();
  const [updateCatalogVariants] = useUpdateCatalogVariantsMutation();
  const [replaceCatalogProductMedia] = useReplaceCatalogProductMediaMutation();
  const [restockCatalogProduct, { isLoading: isRestocking }] = useRestockCatalogProductMutation();
  const [adjustCatalogProductInventory, { isLoading: isAdjusting }] =
    useAdjustCatalogProductInventoryMutation();
  const [updateCatalogProductPublishing, { isLoading: isSavingPublishing }] =
    useUpdateCatalogProductPublishingMutation();

  const productFamily = coreForm?.productFamily || workspace?.product?.productFamily || "shoes";
  const { data: brands = [] } = useFetchBrandsQuery(productFamily, { skip: !productFamily });
  const { data: merchandisingTypes = [] } = useFetchTypesQuery(productFamily, { skip: !productFamily });
  const { data: colorRecords = [] } = useFetchColorsQuery();
  const { data: locations = [] } = useFetchLocationsQuery();

  // ── Hydration: server state → drafts, without wiping unsaved edits ──────
  const hydrate = useCallback((ws, forced = new Set()) => {
    if (!ws) return;
    const previous = baselineRef.current;
    const previousVariantBase = variantBaseDraftsRef.current;
    const structureFull = mapWorkspaceToStructureForm(ws);
    const nextCore = mapWorkspaceToCoreForm(ws);
    const nextPricing = pickPricing(structureFull);
    const nextStructure = omitPricing(structureFull);
    const nextVariants = mapWorkspaceToVariantDrafts(ws);
    const nextMedia = mapWorkspaceToMediaDrafts(ws);

    const keepDraft = (section, current, keyFn) =>
      Boolean(previous) && !forced.has(section) && current !== null && keyFn(current) !== previous[section];

    setCoreForm((current) => (keepDraft("core", current, coreKey) ? current : nextCore));
    setPricingForm((current) => (keepDraft("pricing", current, pricingKey) ? current : nextPricing));
    setStructureForm((current) => (keepDraft("structure", current, structureKey) ? current : nextStructure));
    setMediaDrafts((current) => (keepDraft("media", current, mediaKey) ? current : nextMedia));
    setVariantDrafts((current) =>
      keepDraft("variants", current, variantsKey)
        ? mergeVariantDrafts(current, previousVariantBase, nextVariants)
        : nextVariants
    );

    const nextBaseline = {
      core: coreKey(nextCore),
      pricing: pricingKey(nextPricing),
      structure: structureKey(nextStructure),
      variants: variantsKey(nextVariants),
      media: mediaKey(nextMedia),
      pricingValues: nextPricing,
    };
    baselineRef.current = nextBaseline;
    variantBaseDraftsRef.current = nextVariants;
    setBaseline(nextBaseline);
  }, []);

  useEffect(() => {
    hydrate(workspace);
  }, [workspace, hydrate]);

  // ── Derived data ────────────────────────────────────────────────────────
  const sourceVariants = useMemo(() => workspace?.variants || [], [workspace?.variants]);
  const liveVariants = useMemo(
    () => sourceVariants.filter((variant) => variant.status !== "archived"),
    [sourceVariants]
  );
  const archivedVariants = useMemo(
    () => sourceVariants.filter((variant) => variant.status === "archived"),
    [sourceVariants]
  );
  const liveVariantIds = useMemo(() => new Set(liveVariants.map((variant) => variant.id)), [liveVariants]);
  /** Drafts of variants that are live on the server (archived ones are read-only). */
  const liveVariantDrafts = useMemo(
    () => (variantDrafts || []).filter((draft) => liveVariantIds.has(draft.id)),
    [variantDrafts, liveVariantIds]
  );
  const productColors = useMemo(
    () => buildProductColors(workspace?.options, liveVariants),
    [workspace?.options, liveVariants]
  );
  const colorOption = useMemo(() => findColorOption(workspace?.options), [workspace?.options]);
  const inventoryMode = workspace?.product?.inventoryMode || "aggregate";

  // ── Dirty state per section ─────────────────────────────────────────────
  const dirty = useMemo(() => {
    if (!baseline || !coreForm) return {};
    return {
      core: coreKey(coreForm) !== baseline.core,
      pricing: pricingKey(pricingForm) !== baseline.pricing,
      variants: liveVariantDrafts.some((draft) =>
        variantDraftHasUnsavedChanges(
          draft,
          sourceVariants.find((variant) => variant.id === draft.id)
        )
      ),
      media: mediaKey(mediaDrafts) !== baseline.media,
      structure: structureKey(structureForm) !== baseline.structure,
    };
  }, [baseline, coreForm, pricingForm, liveVariantDrafts, sourceVariants, mediaDrafts, structureForm]);

  // Structure changes are saved from their own confirm flow, not the save bar.
  const savableDirtySections = ["core", "pricing", "variants", "media"].filter((key) => dirty[key]);
  const hasUnsavedChanges = SECTIONS.some((key) => dirty[key]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // In-app links (sidebar, navbar, back link) ask before discarding edits.
  useUnsavedLinkGuard(hasUnsavedChanges, tt("leaveConfirm"));

  // ── Notifications ───────────────────────────────────────────────────────
  const showSuccess = useCallback((message) => {
    notificationRef.current?.showNotification(message, "success");
  }, []);
  const showError = useCallback((message) => {
    notificationRef.current?.showNotification(message, "danger");
  }, []);

  // ── Tabs & deep links ───────────────────────────────────────────────────
  const selectTab = useCallback(
    (tab) => {
      if (!STUDIO_TABS.includes(tab)) return;
      setActiveTab(tab);
      navigate(`${location.pathname}#${tab}`, { replace: true, state: null });
      window.requestAnimationFrame?.(() => {
        document.getElementById("pse-tabs")?.scrollIntoView?.({ block: "nearest" });
      });
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    setActiveTab(resolveTabFromHash(location.hash));
  }, [location.hash]);

  // Deep links carried in router state, consumed once:
  // - stock notifications: #inventory + a variant to highlight;
  // - the products-list scan panel: open restock/adjust for a variant.
  const hasWorkspace = Boolean(workspace);
  useEffect(() => {
    const state = location.state;
    if (!hasWorkspace || !state) return;
    const variantId = Number(state.notificationVariantId || 0);
    const action = state.catalogAction;
    const createdReceipt = state.createdReceipt;
    if (!variantId && !action && !createdReceipt) return;

    if (createdReceipt?.id && createdReceipt.labelsOutstanding > 0) setCreatedReceipt(createdReceipt);
    if (variantId) {
      setActiveTab("inventory");
      setFocusVariantId(variantId);
      window.setTimeout(() => {
        document
          .getElementById(`catalog-inventory-variant-${variantId}`)
          ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }, 80);
    }
    if (action?.type === "adjust") setAdjustState({ open: true, variantId: action.variantId || null });
    if (action?.type === "restock") setRestockState({ open: true, variantId: action.variantId || null });
    navigate(`${location.pathname}${location.hash}`, { replace: true, state: null });
  }, [hasWorkspace, location.hash, location.pathname, location.state, navigate]);

  // ── Draft editors ───────────────────────────────────────────────────────
  const updateCore = useCallback((name, value) => {
    setCoreForm((current) => ({ ...current, [name]: value }));
  }, []);
  const updateEnrichment = useCallback((name, value) => {
    setCoreForm((current) => ({ ...current, enrichment: { ...current.enrichment, [name]: value } }));
  }, []);
  const updatePricing = useCallback((name, value) => {
    setPricingForm((current) => ({ ...current, [name]: value }));
  }, []);
  const updateVariant = useCallback((variantId, field, value) => {
    setVariantDrafts((current) =>
      current.map((variant) => {
        if (variant.id === variantId) return { ...variant, [field]: value };
        if (field === "isDefault" && value) return { ...variant, isDefault: false };
        return variant;
      })
    );
  }, []);
  const setVariantStatusBulk = useCallback((variantIds, status) => {
    const ids = new Set(variantIds);
    setVariantDrafts((current) =>
      current.map((variant) => (ids.has(variant.id) ? { ...variant, status } : variant))
    );
  }, []);

  // ── Saving (each section keeps its own mutation) ────────────────────────
  const buildVariantSaveDrafts = useCallback(() => {
    const merged = { ...structureForm, ...pricingForm };
    if (!dirty.pricing) return liveVariantDrafts;
    const priced = applySharedPricingToVariantDrafts(
      liveVariantDrafts,
      merged,
      baselineRef.current?.pricingValues
    );
    const pricedById = new Map(priced.map((draft) => [draft.id, draft]));
    return liveVariantDrafts.map((draft) => pricedById.get(draft.id) || draft);
  }, [dirty.pricing, liveVariantDrafts, pricingForm, structureForm]);

  const sectionLabel = useCallback(
    (key) =>
      ({
        core: tt("sectionCore"),
        pricing: tt("sectionPricing"),
        variants: tt("sectionVariants"),
        media: tt("sectionMedia"),
        structure: tt("sectionStructure"),
      }[key]),
    [tt]
  );

  const handleSaveAll = useCallback(async () => {
    if (!savableDirtySections.length || isSavingAll) return;
    setIsSavingAll(true);
    const saved = [];
    const failed = [];
    const errors = [];

    if (dirty.core) {
      try {
        await updateCatalogProductCore({
          productId,
          body: buildCorePayload(coreForm, buildEnrichmentPayload(coreForm.enrichment)),
        }).unwrap();
        saved.push("core");
      } catch (error) {
        failed.push("core");
        errors.push(humanizeCatalogError(error, tt, "errCore"));
      }
    }

    if ((dirty.pricing || dirty.variants) && liveVariantDrafts.length) {
      try {
        await updateCatalogVariants({
          productId,
          body: { items: mapVariantDraftsToSaveItems(buildVariantSaveDrafts()) },
        }).unwrap();
        if (dirty.pricing) saved.push("pricing");
        if (dirty.variants) saved.push("variants");
      } catch (error) {
        if (dirty.pricing) failed.push("pricing");
        if (dirty.variants) failed.push("variants");
        errors.push(humanizeCatalogError(error, tt, "errVariants"));
      }
    }

    if (dirty.media) {
      try {
        // Drop colour covers an edit elsewhere made invalid, so the payload is
        // never one the API rejects. Order and the product cover are untouched.
        const draftsToSave = pruneInvalidColorCovers(mediaDrafts, productColors);
        await replaceCatalogProductMedia({
          productId,
          body: { items: mapMediaDraftsToSaveItems(draftsToSave) },
        }).unwrap();
        saved.push("media");
      } catch (error) {
        failed.push("media");
        errors.push(humanizeCatalogError(error, tt, "errMedia"));
      }
    }

    try {
      const fresh = await refetch();
      hydrate(fresh?.data || workspace, new Set(saved));
    } catch (_error) {
      // The mutations already invalidated the cache; the effect will hydrate.
    }
    setIsSavingAll(false);

    if (saved.length) {
      showSuccess(fill(tt("saveResultOk"), { sections: saved.map(sectionLabel).join(", ") }));
    }
    if (failed.length) {
      showError(
        `${fill(tt("saveResultFailed"), { sections: failed.map(sectionLabel).join(", ") })} ${errors.join(" ")}`
      );
    }
  }, [
    buildVariantSaveDrafts,
    coreForm,
    dirty,
    hydrate,
    isSavingAll,
    liveVariantDrafts.length,
    mediaDrafts,
    productColors,
    productId,
    refetch,
    replaceCatalogProductMedia,
    savableDirtySections.length,
    sectionLabel,
    showError,
    showSuccess,
    tt,
    updateCatalogProductCore,
    updateCatalogVariants,
    workspace,
  ]);

  const handleDiscardAll = useCallback(() => {
    hydrate(workspace, new Set(SECTIONS));
  }, [hydrate, workspace]);

  /**
   * Same sequence as before the redesign: unsaved variant/price edits are
   * persisted first, then the structure — so structure pricing never
   * overwrites a price the user just typed.
   */
  const handleSaveStructure = useCallback(async (formOverride = null) => {
    const nextStructureForm = formOverride || structureForm;
    let didPersistVariants = false;
    try {
      if ((dirty.pricing || dirty.variants) && liveVariantDrafts.length) {
        try {
          await updateCatalogVariants({
            productId,
            body: { items: mapVariantDraftsToSaveItems(buildVariantSaveDrafts()) },
          }).unwrap();
          didPersistVariants = true;
        } catch (error) {
          showError(humanizeCatalogError(error, tt, "errVariants"));
          return false;
        }
      }

      const response = await saveCatalogProductStructure({
        productId,
        body: buildStructurePayload({ ...nextStructureForm, ...pricingForm }),
      }).unwrap();

      const fresh = await refetch();
      hydrate(fresh?.data || workspace, new Set(["structure", "pricing", "variants"]));

      const summary = response?.structureChangeSummary || {};
      const parts = [
        summary.created > 0 ? fill(tt("stSavedCreated"), { count: summary.created }) : null,
        summary.restored > 0 ? fill(tt("stSavedRestored"), { count: summary.restored }) : null,
        summary.archived > 0 ? fill(tt("stSavedArchived"), { count: summary.archived }) : null,
      ].filter(Boolean);
      showSuccess(parts.length ? `${tt("stSaved")} ${parts.join(", ")}.` : tt("stSaved"));
      return true;
    } catch (error) {
      if (didPersistVariants) {
        const fresh = await refetch();
        hydrate(fresh?.data || workspace, new Set(["pricing", "variants"]));
      }
      showError(humanizeCatalogError(error, tt, "errStructure"));
      return false;
    }
  }, [
    buildVariantSaveDrafts,
    dirty.pricing,
    dirty.variants,
    hydrate,
    liveVariantDrafts.length,
    pricingForm,
    productId,
    refetch,
    saveCatalogProductStructure,
    showError,
    showSuccess,
    structureForm,
    tt,
    updateCatalogVariants,
    workspace,
  ]);

  const handlePublishingChange = useCallback(
    async (statusKey) => {
      try {
        await updateCatalogProductPublishing({
          productId,
          body: { status: STATUS_TO_API[statusKey] },
        }).unwrap();
        const fresh = await refetch();
        hydrate(fresh?.data || workspace);
        showSuccess(
          tt(
            {
              published: "pbSavedPublished",
              posOnly: "pbSavedPosOnly",
              draft: "pbSavedDraft",
              archived: "pbSavedArchived",
            }[statusKey]
          )
        );
        return true;
      } catch (error) {
        showError(humanizeCatalogError(error, tt, "pbError"));
        return false;
      }
    },
    [hydrate, productId, refetch, showError, showSuccess, tt, updateCatalogProductPublishing, workspace]
  );

  // ── Media ───────────────────────────────────────────────────────────────
  const filledMediaCount = (mediaDrafts || []).filter(isFilledMedia).length;

  const handleMediaUpload = useCallback(
    async (fileList, { variantIds = [] } = {}) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const left = MAX_PRODUCT_MEDIA - filledMediaCount;
      if (left <= 0) {
        showError(fill(tt("mdLimitReached"), { max: MAX_PRODUCT_MEDIA }));
        return;
      }
      if (files.length > left) {
        showError(fill(tt("mdLimitExceeded"), { selected: files.length, left }));
        return;
      }
      const invalidType = files.find((file) => file.type && !ALLOWED_UPLOAD_TYPES.includes(file.type));
      if (invalidType) {
        showError(fill(tt("mdInvalidType"), { name: invalidType.name }));
        return;
      }
      const tooLarge = files.find((file) => file.size > MAX_UPLOAD_BYTES);
      if (tooLarge) {
        showError(fill(tt("mdTooLarge"), { name: tooLarge.name }));
        return;
      }

      const uploadedItems = [];
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          const uploaded = await uploadCatalogMediaAsset(formData).unwrap();
          uploadedItems.push({
            ...createEmptyMediaItem(),
            storageKey: uploaded?.storageKey || "",
            sourceUrl: uploaded?.sourceUrl || "",
            originalName: uploaded?.originalName || file.name,
            thumbnailKey: uploaded?.thumbnailKey || "",
            cardKey: uploaded?.cardKey || "",
            fullKey: uploaded?.fullKey || "",
            variantIds: [...variantIds],
          });
        } catch (error) {
          showError(`${file.name}: ${humanizeCatalogError(error, tt, "errUpload")}`);
        }
      }

      if (!uploadedItems.length) return;
      setMediaDrafts((current) => {
        const merged = [...(current || []).filter(isFilledMedia), ...uploadedItems];
        if (!merged.some((item) => item.isCover) && merged[0]) {
          merged[0] = { ...merged[0], isCover: true };
        }
        return merged;
      });
      showSuccess(fill(tt("mdUploadedPending"), { count: uploadedItems.length }));
    },
    [filledMediaCount, showError, showSuccess, tt, uploadCatalogMediaAsset]
  );

  // ── Stock ───────────────────────────────────────────────────────────────
  const handleSubmitRestock = useCallback(
    async (payload) => {
      try {
        // Serialized restocks create new units; only those need new labels.
        const unitIdsBefore = new Set(
          sourceVariants.flatMap((variant) => (variant.inventory?.units || []).map((unit) => unit.id))
        );
        const response = await restockCatalogProduct({ productId, body: payload }).unwrap();
        const refreshed = await refetch();
        // The restock response is the updated workspace from the same request;
        // a refetch that is already in flight may still hold the old units.
        const nextWorkspace = response?.variants ? response : refreshed?.data?.variants ? refreshed.data : response;
        const variantMap = new Map((nextWorkspace?.variants || sourceVariants).map((variant) => [variant.id, variant]));
        const quantityByVariantId = new Map(payload.items.map((item) => [item.variantId, item.quantity]));
        const serialized = isSerializedProduct(nextWorkspace?.product || workspace?.product);

        const printableItems = buildCatalogPrintItemsFromWorkspace(nextWorkspace).filter(
          (item) =>
            quantityByVariantId.has(item.variantId) &&
            !item.blockedReason &&
            (!serialized || !unitIdsBefore.has(item.unitId))
        );
        const items = payload.items.map((item) => ({
          variantId: item.variantId,
          variant: variantMap.get(item.variantId),
          quantity: item.quantity,
          // Serialized: the new units' own barcodes are the labels, never the variant barcode.
          unitBarcodes: serialized
            ? (variantMap.get(item.variantId)?.inventory?.units || [])
                .filter((unit) => !unitIdsBefore.has(unit.id))
                .map((unit) => unit.barcode)
                .filter(Boolean)
            : null,
        }));

        return {
          items,
          // Labels this restock needs (for "Print X labels" in the Print Center).
          receipt: response?.receipt || null,
          variantCount: items.length,
          totalUnits: items.reduce((sum, item) => sum + item.quantity, 0),
          printableItems,
          // Exactly the received quantity — never the variant's whole stock.
          initialCopiesById: printableItems.reduce((accumulator, item) => {
            accumulator[item.printKey] = serialized ? 1 : quantityByVariantId.get(item.variantId);
            return accumulator;
          }, {}),
        };
      } catch (error) {
        showError(humanizeCatalogError(error, tt, "rsError"));
        return null;
      }
    },
    [productId, refetch, restockCatalogProduct, showError, sourceVariants, tt, workspace?.product]
  );

  const handleAdjustInventory = useCallback(
    async (payload) => {
      try {
        await adjustCatalogProductInventory({ productId, body: payload }).unwrap();
        await refetch();
        setAdjustState({ open: false, variantId: null });
        showSuccess(tt("adSaved"));
        return true;
      } catch (error) {
        showError(humanizeCatalogError(error, tt, "adError"));
        return false;
      }
    },
    [adjustCatalogProductInventory, productId, refetch, showError, showSuccess, tt]
  );

  // ── Printing ────────────────────────────────────────────────────────────
  const openPrint = useCallback(
    (variantIds, copies = null) => {
      const ids = new Set(variantIds);
      const items = buildCatalogPrintItemsFromWorkspace(workspace).filter((item) => ids.has(item.variantId));
      if (!items.length) {
        showError(tt("noPrintable"));
        return;
      }
      setPrintState({ open: true, items, copies });
    },
    [showError, tt, workspace]
  );
  const openPrintItems = useCallback(
    (items, copies) => {
      if (!items?.length) {
        showError(tt("rsNoPrintable"));
        return;
      }
      setPrintState({ open: true, items, copies: copies || null });
    },
    [showError, tt]
  );

  const productFamilyOptions = useMemo(() => getProductFamilyOptions(t), [t]);

  // ── Render ──────────────────────────────────────────────────────────────
  if (!workspace || !coreForm) {
    if (isWorkspaceError && !isFetchingWorkspace) {
      return (
        <div className="content pse pse-page">
          <NotificationComponent ref={notificationRef} />
          <div className="pse-card pse-card--center">
            <EmptyState
              icon="alert"
              title={tt("loadErrorTitle")}
              action={
                <div className="pse-inline">
                  <Btn variant="primary" icon="refresh" onClick={() => refetch()}>
                    {tt("retry")}
                  </Btn>
                  <Link to="/admin/catalog/products" className="pse-btn pse-btn--secondary pse-btn--md">
                    <Icon name="arrowLeft" size={16} />
                    <span className="pse-btn__label">{tt("back")}</span>
                  </Link>
                </div>
              }
            >
              {tt("loadErrorCopy")}
            </EmptyState>
          </div>
        </div>
      );
    }
    return (
      <div className="content pse pse-page pse-page--loading">
        <NotificationComponent ref={notificationRef} />
        <Spinner label={tt("loading")} />
      </div>
    );
  }

  const studio = {
    t,
    tt,
    workspace,
    productId,
    coreForm,
    pricingForm,
    structureForm,
    variantDrafts: variantDrafts || [],
    liveVariantDrafts,
    liveVariants,
    archivedVariants,
    mediaDrafts: mediaDrafts || [],
    productColors,
    colorOption,
    colorRecords,
    brands,
    merchandisingTypes,
    locations,
    productFamilyOptions,
    productFamily,
    inventoryMode,
    dirty,
    hasUnsavedChanges,
    isUploadingMedia,
    isSavingStructure,
    isSavingPublishing,
    isFetchingWorkspace,
    recentMovements: recentHistoryQuery.data?.items || [],
    isFetchingRecentMovements: recentHistoryQuery.isFetching,
    focusVariantId,
    setFocusVariantId,
    isStructureEditorOpen,
    setIsStructureEditorOpen,
    selectTab,
    updateCore,
    updateEnrichment,
    updatePricing,
    updateVariant,
    setVariantStatusBulk,
    setStructureForm,
    setMediaDrafts,
    handleMediaUpload,
    handleSaveStructure,
    handlePublishingChange,
    openRestock: (variantId = null) => setRestockState({ open: true, variantId }),
    openAdjust: (variantId = null) => setAdjustState({ open: true, variantId }),
    openPrint,
    showSuccess,
    showError,
  };

  const tabContent = {
    overview: <OverviewTab studio={studio} />,
    media: <MediaTab studio={studio} />,
    variants: <VariantsTab studio={studio} />,
    inventory: <StockTab studio={studio} />,
    details: <DetailsTab studio={studio} />,
    publishing: <PublishingTab studio={studio} />,
    history: <HistoryTab studio={studio} />,
  }[activeTab];

  return (
    <div className="content pse pse-page">
      <NotificationComponent ref={notificationRef} />
      <StudioShell
        studio={studio}
        activeTab={activeTab}
        onSelectTab={selectTab}
        dirtySections={savableDirtySections.map(sectionLabel)}
        structureDirty={Boolean(dirty.structure)}
        isSavingAll={isSavingAll}
        onSaveAll={handleSaveAll}
        onDiscardAll={handleDiscardAll}
        onRefresh={() => refetch()}
      >
        <div
          role="tabpanel"
          id={`pse-panel-${activeTab}`}
          aria-labelledby={`pse-tab-${activeTab}`}
          className="pse-panel"
        >
          {createdReceipt ? (
            <Notice
              tone="info"
              icon="printer"
              className="pse-created-labels"
              title={tt("createdLabelsTitle")}
              action={
                <div className="pse-inline">
                  <Btn variant="ghost" size="sm" onClick={() => setCreatedReceipt(null)}>
                    {tt("createdLabelsLater")}
                  </Btn>
                  <Btn
                    variant="primary"
                    size="sm"
                    icon="printer"
                    onClick={() => navigate(printCenterReceiptPath([createdReceipt.id]))}
                  >
                    {fill(tt(createdReceipt.labelsOutstanding === 1 ? "rsPrintLabelsOne" : "rsPrintLabels"), {
                      count: createdReceipt.labelsOutstanding,
                    })}
                  </Btn>
                </div>
              }
            >
              {fill(tt(createdReceipt.labelsOutstanding === 1 ? "createdLabelsCopyOne" : "createdLabelsCopy"), {
                count: createdReceipt.labelsOutstanding,
              })}
            </Notice>
          ) : null}
          {tabContent}
        </div>
      </StudioShell>

      <RestockDrawer
        tt={tt}
        isOpen={restockState.open}
        defaultVariantId={restockState.variantId}
        onClose={() => setRestockState({ open: false, variantId: null })}
        variants={liveVariants}
        media={mediaDrafts || []}
        colorOption={colorOption}
        locations={locations}
        inventoryMode={inventoryMode}
        onSubmit={handleSubmitRestock}
        onPrint={(result) => openPrintItems(result.printableItems, result.initialCopiesById)}
        onOpenPrintCenter={(receipt) => navigate(printCenterReceiptPath([receipt.id]))}
        isSaving={isRestocking}
      />

      <AdjustStockDrawer
        tt={tt}
        isOpen={adjustState.open}
        defaultVariantId={adjustState.variantId}
        onClose={() => setAdjustState({ open: false, variantId: null })}
        variants={liveVariants}
        locations={locations}
        onSubmit={handleAdjustInventory}
        isSaving={isAdjusting}
      />

      {/* The shared print panel is styled by the catalog admin theme, whose
          light-mode tokens live on .catalog-product-studio. */}
      <div className="catalog-product-studio pse-print-scope">
        <CatalogBarcodePrintModal
          isOpen={printState.open}
          toggle={() => setPrintState({ open: false, items: [], copies: null })}
          items={printState.items}
          initialCopiesById={printState.copies}
          onPrinted={() => showSuccess(tt("printed"))}
        />
      </div>
    </div>
  );
};

export default ProductStudioEditWorkspace;
